import MatchingChip from 'components/Chips/MatchingChip';
import MenteeStatusChip from 'components/Chips/MenteeStatusChip';
import CopyText from 'components/CopyText';
import { ActionDrawer } from 'components/Drawer';
import CheckboxList from 'components/Drawer/CheckboxList';
import EllipsisText from 'components/EllipsisText';
import Checkbox from 'components/form/Inputs/Checkbox';
import LabelledCheckbox from 'components/form/Inputs/LabelledCheckbox';
import SettingsIcon from 'components/icons/Settings';
import List from 'components/list';
import {
  ChooseMentor,
  ChooseMentorContainer,
} from 'components/list/ChooseMentor';
import SettingsContainer from 'components/list/SettingsContainer';
import { BoldTd, Td } from 'components/list/Td';
import { getOrderTh, Th } from 'components/list/Th';
import { getColumnNumber } from 'components/list/utils';
import CONSIDERATIONS from 'enums/Considerations';
import EXPERTISE from 'enums/Expertise';
import FORMATIONS from 'enums/Formation';
import FORMATION_PROJECT_MATCHES from 'enums/FormationProjectMatches';
import FORMATION_STATES from 'enums/FormationState';
import HOBBIES from 'enums/Hobby';
import { MENTEE_PRIORITY_LEVELS } from 'enums/PriorityLevels';
import STUDY_LEVELS from 'enums/StudyLevels';
import VOLUNTEER_STATES from 'enums/VolunteerState';
import React, { FC, useContext, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { setActiveColumns } from 'store/actions/admin/mentorLists';
import { StoreState } from 'store/reducers';
import { Action } from 'types/typeguards/reducerActions';
import { getLocaleDate } from 'utils/time';
import { AssociateContext, sortableColumns } from '.';

export const columnNames = [
  'matching',
  'softMatching',
  'hardMatching',
  'firstName',
  'lastName',
  'email',
  'createdAt',
  'region',
  'postalCode',
  'status',
  'jobSearch',
  'careerPlan',
  'studies',
  'entrepreneurship',
  'completedFormation',
  'validatedDiploma',
  'sharing',
  'expertiseFields',
  'formationMatchingProject',
  'formationState',
  'intendedFormation',
  'intendedDiploma',
  'consideration',
  'hobby',
  'volunteerState',
] as const;
export type ColumnNames = typeof columnNames[number];
export type Columns = {
  [key in ColumnNames]: boolean;
};
const OrderTh = getOrderTh<typeof sortableColumns[number]>();

export const initialActiveColumns: Columns = {
  matching: true,
  softMatching: false,
  hardMatching: false,
  firstName: true,
  lastName: true,
  email: true,
  createdAt: true,
  region: false,
  postalCode: false,
  status: false,
  // Mentee form questions
  jobSearch: false,
  careerPlan: false,
  studies: false,
  entrepreneurship: false,
  completedFormation: false,
  validatedDiploma: false,
  sharing: false,
  expertiseFields: false,
  formationMatchingProject: false,
  formationState: false,
  intendedFormation: false,
  intendedDiploma: false,
  consideration: false,
  hobby: false,
  volunteerState: false,
};

type Props = {
  activeColumns: Columns;
  setActiveColumns: (activeColumns: Columns) => void;
};
const AssociateList: FC<Props> = ({ activeColumns, setActiveColumns }) => {
  const { mentor, mentees, pages, page, setPage, loading, order, setOrder } =
    useContext(AssociateContext);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [selectedColumns, setSelectedColumns] = useState(activeColumns);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setSelectedColumns({
      ...selectedColumns,
      [e.target.name]: e.target.checked,
    });
  };

  const columnNumber = useMemo(() => {
    return getColumnNumber(activeColumns, false);
  }, [activeColumns]);

  return (
    <>
      <ActionDrawer
        open={settingsOpen}
        close={() => setSettingsOpen(false)}
        title="Colonnes mentorés"
        subtitle="Activer les colonnes que vous souhaitez"
        cancel={() => {
          setSelectedColumns(activeColumns);
        }}
        apply={() => {
          setActiveColumns(selectedColumns);
        }}
      >
        <CheckboxList>
          <LabelledCheckbox
            label="Matching"
            checked={selectedColumns.matching}
            name="matching"
            onChange={handleChange}
          />
          {/*
            DISABLED
            
          <LabelledCheckbox
            label="Matching soft"
            checked={selectedColumns.softMatching}
            name="softMatching"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Matching hard"
            checked={selectedColumns.hardMatching}
            name="hardMatching"
            onChange={handleChange}
          /> */}
          <LabelledCheckbox
            label="Email"
            checked={selectedColumns.email}
            name="email"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Date d'inscription"
            checked={selectedColumns.createdAt}
            name="createdAt"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Région"
            checked={selectedColumns.region}
            name="region"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Code postal"
            checked={selectedColumns.postalCode}
            name="postalCode"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Statut du mentorat"
            checked={selectedColumns.status}
            name="status"
            onChange={handleChange}
          />
          {/* Mentee form questions */}
          <LabelledCheckbox
            label="Recherche d'emploi"
            checked={selectedColumns.jobSearch}
            name="jobSearch"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Carrière"
            checked={selectedColumns.careerPlan}
            name="careerPlan"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Études"
            checked={selectedColumns.studies}
            name="studies"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Entreprenariat"
            checked={selectedColumns.entrepreneurship}
            name="entrepreneurship"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Partage"
            checked={selectedColumns.sharing}
            name="sharing"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Domaines de compétence"
            checked={selectedColumns.expertiseFields}
            name="expertiseFields"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Formation effectuée"
            checked={selectedColumns.completedFormation}
            name="completedFormation"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Plus haut niveau de diplôme"
            checked={selectedColumns.validatedDiploma}
            name="validatedDiploma"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Formation en accord avec le projet ?"
            checked={selectedColumns.formationMatchingProject}
            name="formationMatchingProject"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="État de la formation"
            checked={selectedColumns.formationState}
            name="formationState"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Formation correspondant au projet"
            checked={selectedColumns.intendedFormation}
            name="intendedFormation"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Niveau de diplôme visé"
            checked={selectedColumns.intendedDiploma}
            name="intendedDiploma"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Mode de vie"
            checked={selectedColumns.consideration}
            name="consideration"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Passe temps"
            checked={selectedColumns.hobby}
            name="hobby"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="État du service civique"
            checked={selectedColumns.volunteerState}
            name="volunteerState"
            onChange={handleChange}
          />
        </CheckboxList>
      </ActionDrawer>
      <List
        activeColumns={activeColumns}
        columnNumber={columnNumber}
        isEmpty={mentees?.length === 0}
        emptyText="Aucun mentoré n'est disponible pour cette recherche"
        pages={pages}
        page={page}
        setPage={setPage}
        loading={loading}
      >
        <OrderTh name="matching" order={order} setOrder={setOrder}>
          Matching
        </OrderTh>
        <Th name="softMatching">Matching soft</Th>
        <Th name="hardMatching">Matching hard</Th>
        <OrderTh name="firstName" order={order} setOrder={setOrder}>
          Prénom
        </OrderTh>
        <OrderTh name="lastName" order={order} setOrder={setOrder}>
          Nom
        </OrderTh>
        <Th name="email">Email</Th>
        <OrderTh name="createdAt" order={order} setOrder={setOrder}>
          Date d'inscription
        </OrderTh>
        <Th name="region">Région</Th>
        <Th name="postalCode">Code postal</Th>
        {/* Mentee form questions */}
        <Th name="status">Statut du mentorat</Th>
        <Th name="jobSearch">Recherche d'emploi</Th>
        <Th name="careerPlan">Carrière</Th>
        <Th name="studies">Études</Th>
        <Th name="entrepreneurship">Entreprenariat</Th>
        <Th name="sharing">Partage</Th>
        <Th name="expertiseFields">Compétences</Th>
        <Th name="completedFormation">Formation effectuée</Th>
        <Th name="validatedDiploma">Niveau de diplôme</Th>
        <Th name="formationMatchingProject">Formation en accord ?</Th>
        <Th name="formationState">État de la formation</Th>
        <Th name="intendedFormation">Formation correspondante</Th>
        <Th name="intendedDiploma">Niveau visé</Th>
        <Th name="consideration">Mode de vie</Th>
        <Th name="hobby">Passe temps</Th>
        <Th name="volunteerState">État service civique</Th>
        <Th name="settings">
          <SettingsContainer onClick={() => setSettingsOpen(true)}>
            <SettingsIcon />
          </SettingsContainer>{' '}
        </Th>
        {mentees?.map((mentee, idx) => (
          <React.Fragment key={idx}>
            <Td name="matching">
              {mentee.MenteeMatching && mentee.MenteeMatching[0] && (
                <MatchingChip value={mentee.MenteeMatching[0].value} />
              )}
            </Td>
            <Td name="softMatching">
              {mentee.MenteeMatching && typeof mentee.MenteeMatching[0] && (
                <MatchingChip
                  value={mentee.MenteeMatching[0]?.softValue || 0}
                />
              )}
            </Td>
            <Td name="hardMatching">
              {mentee.MenteeMatching && typeof mentee.MenteeMatching[0] && (
                <MatchingChip
                  value={mentee.MenteeMatching[0]?.hardValue || 0}
                />
              )}
            </Td>
            <BoldTd name="firstName">
              <Link to={`/admin/mentoring/mentees/${mentee.id}/mentoring/`}>
                <EllipsisText text={mentee.firstName || ''} />
              </Link>
            </BoldTd>
            <BoldTd name="lastName">
              <Link to={`/admin/mentoring/mentees/${mentee.id}/mentoring/`}>
                <EllipsisText text={mentee.lastName || ''} />
              </Link>
            </BoldTd>

            <Td name="email">
              <EllipsisText text={mentee.email || ''} />
              <CopyText text="" copiedText={mentee.email || ''} />
            </Td>
            <Td name="createdAt">
              {mentee.createdAt && getLocaleDate(mentee.createdAt)}
            </Td>
            <Td name="region">{mentee.region}</Td>
            <Td name="postalCode">{mentee.postalCode}</Td>
            <Td name="status">
              <MenteeStatusChip
                status={mentee.menteeStatus!}
                mentorshipStatus={
                  (mentee.MenteeMentorship &&
                    mentee.MenteeMentorship[0]?.status) ||
                  'ONGOING'
                }
              />
            </Td>
            {/* Mentee form questions */}
            <Td name="jobSearch">
              {mentee.MenteeForm?.jobSearch &&
                MENTEE_PRIORITY_LEVELS[mentee.MenteeForm.jobSearch]}
            </Td>
            <Td name="careerPlan">
              {mentee.MenteeForm?.careerPlan &&
                MENTEE_PRIORITY_LEVELS[mentee.MenteeForm.careerPlan]}
            </Td>
            <Td name="studies">
              {mentee.MenteeForm?.studies &&
                MENTEE_PRIORITY_LEVELS[mentee.MenteeForm.studies]}
            </Td>
            <Td name="entrepreneurship">
              {mentee.MenteeForm?.entrepreneurship &&
                MENTEE_PRIORITY_LEVELS[mentee.MenteeForm.entrepreneurship]}
            </Td>
            <Td name="sharing">
              <Checkbox readOnly checked={mentee.MenteeForm?.sharing} />
            </Td>
            <Td name="expertiseFields">
              <EllipsisText
                text={
                  mentee.MenteeForm?.expertiseFields
                    ?.map(
                      (expertiseField) => EXPERTISE[expertiseField.expertise],
                    )
                    .join('; ') || ''
                }
              />
            </Td>
            <Td name="completedFormation">
              <EllipsisText
                text={
                  mentee.MenteeForm?.completedFormation
                    ? FORMATIONS[mentee.MenteeForm.completedFormation]
                    : ''
                }
              />
            </Td>
            <Td name="validatedDiploma">
              {mentee.MenteeForm?.validatedDiploma &&
                STUDY_LEVELS[mentee.MenteeForm.validatedDiploma]}
            </Td>
            <Td name="formationMatchingProject">
              {mentee.MenteeForm?.formationMatchingProject &&
                FORMATION_PROJECT_MATCHES[
                  mentee.MenteeForm.formationMatchingProject
                ]}
            </Td>
            <Td name="formationState">
              {mentee.MenteeForm?.formationState &&
                FORMATION_STATES[mentee.MenteeForm.formationState]}
            </Td>
            <Td name="intendedFormation">
              {mentee.MenteeForm?.intendedFormation &&
                FORMATIONS[mentee.MenteeForm.intendedFormation]}
            </Td>
            <Td name="intendedDiploma">
              {mentee.MenteeForm?.intendedDiploma &&
                STUDY_LEVELS[mentee.MenteeForm.intendedDiploma]}
            </Td>
            <Td name="consideration">
              {mentee.MenteeForm?.consideration &&
                CONSIDERATIONS[mentee.MenteeForm.consideration]}
            </Td>
            <Td name="hobby">
              {mentee.MenteeForm?.hobby && HOBBIES[mentee.MenteeForm.hobby]}
            </Td>
            <Td name="volunteerState">
              {mentee.MenteeForm?.volunteerState &&
                VOLUNTEER_STATES[mentee.MenteeForm.volunteerState]}
            </Td>
            <Td name="settings">
              <ChooseMentorContainer>
                <ChooseMentor
                  menteeId={mentee.id || -1}
                  mentorId={mentor?.id || -1}
                  redirect={`/admin/mentoring/mentors/${
                    mentor!.id
                  }/mentoring/ongoing`}
                >
                  Selectionner comme mentor
                </ChooseMentor>
              </ChooseMentorContainer>
            </Td>
          </React.Fragment>
        ))}
      </List>
    </>
  );
};

const mapStateToProps = (state: StoreState) => {
  return {
    activeColumns: state.admin.mentorLists.associate.activeColumns,
  };
};

const mapDispatchToProps = (dispatch: (action: Action) => void) => {
  return {
    setActiveColumns: (activeColumns: Columns) =>
      dispatch(setActiveColumns<Columns>('associate', activeColumns)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(AssociateList);
